import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  CreditCard,
  CircleHelp,
  Compass,
  Languages,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { HelpMenuItems } from "@/components/layout/HelpShortcutButton";
import { LogoMark } from "@/components/layout/Sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TOUR_ANCHOR } from "@/components/product-tour/tourSteps";
import { useProductTour } from "@/components/product-tour/useProductTour";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { hasNavigableAppAccess } from "@/lib/permissions";
import { resolveApiErrorFromException } from "@/lib/apiErrorHandling";
import { initialsFromDisplay } from "@/lib/initials";
import { setUiLanguage } from "@/lib/setUiLanguage";
import { signOutClient } from "@/lib/signOut";
import { switchActiveOrganization } from "@/lib/switchOrganization";
import { cn } from "@/lib/utils";

type Panel = "main" | "theme" | "language" | "organizations" | "help";

function ThemeMenuIcon({
  mounted,
  activeTheme,
}: {
  mounted: boolean;
  activeTheme: string | undefined;
}): ReactElement {
  if (!mounted) {
    return <Sun className="size-4 opacity-50" aria-hidden />;
  }
  if (activeTheme === "light") {
    return <Sun className="size-4" aria-hidden />;
  }
  if (activeTheme === "dark") {
    return <Moon className="size-4" aria-hidden />;
  }
  return <Monitor className="size-4" aria-hidden />;
}

type UserAccountMenuProps = {
  /** Compact avatar trigger; sidebar rail / mobile density. */
  variant?: "header" | "footer" | "topbar";
  /** Mobile header: Help and Settings live in this menu instead of the toolbar. */
  includeChromeShortcuts?: boolean;
};

const accountMenuItemCls = "justify-between gap-2 py-2";
const accountMenuSeparatorCls = "-mx-2 my-2";

function AccountBillingMenuItem(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user?.organization || !user.isOrgAdmin || user.organization.billingExempt) {
    return null;
  }

  return (
    <DropdownMenuItem className={accountMenuItemCls} onClick={() => navigate("/settings/billing")}>
      <span className="flex min-w-0 items-center gap-1.5">
        <CreditCard className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{t("billing.title")}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </DropdownMenuItem>
  );
}

function AccountSettingsMenuItem(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();

  if (!can("nav.settings")) return null;

  return (
    <DropdownMenuItem className={accountMenuItemCls} onClick={() => navigate("/settings")}>
      <span className="flex min-w-0 items-center gap-1.5">
        <Settings className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{t("nav.settings")}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </DropdownMenuItem>
  );
}

function AccountHelpMenuItem({ onOpenHelp }: { onOpenHelp: () => void }): ReactElement {
  const { t } = useTranslation();

  return (
    <DropdownMenuItem
      className={accountMenuItemCls}
      onSelect={(event) => {
        event.preventDefault();
        onOpenHelp();
      }}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <CircleHelp className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{t("help.title")}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </DropdownMenuItem>
  );
}

export function UserAccountMenu({
  variant = "header",
  includeChromeShortcuts = false,
}: UserAccountMenuProps): ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const productTour = useProductTour();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [panel, setPanel] = useState<Panel>("main");
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const displayName = user?.displayName?.trim() || user?.email?.split("@")[0] || t("common.guest");
  const initials = initialsFromDisplay(displayName);
  const avatarUrl = user?.avatarUrl ?? null;
  const topbarSubtitle = user?.organization?.name?.trim() || "";
  const languageValue = i18n.language.startsWith("pt") ? "pt-BR" : "en";
  const organizations = user?.organizations ?? [];
  const showOrgSwitcher = organizations.length > 1;

  const compact = variant === "header";
  const desktopBadge = variant === "topbar";

  const themeValue = (mounted ? theme : "system") ?? "system";

  async function onSelectOrganization(organizationId: string): Promise<void> {
    if (!user || switching) return;
    const selected = organizations.find((org) => org.id === organizationId);
    if (selected?.memberStatus === "invited") {
      navigate(`/accept-invite?organizationId=${encodeURIComponent(organizationId)}`);
      return;
    }
    if (organizationId === user.organization?.id) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      // Hard-reloads the app into the new tenant context (does not return).
      await switchActiveOrganization(organizationId);
    } catch (err) {
      setSwitchError(resolveApiErrorFromException(err));
      setSwitching(false);
    }
  }

  const avatarNode = (size: "sm" | "default" = "sm") => (
    <Avatar size={size} className="shrink-0">
      {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
      <AvatarFallback className="bg-primary text-xs text-primary-foreground">
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  const triggerCls = cn(
    desktopBadge &&
      "h-10 max-w-[16rem] min-w-[9rem] justify-between gap-2 rounded-full border border-border bg-muted/50 px-2.5 py-0 shadow-none hover:bg-muted/80",
    compact && !desktopBadge && "size-8 shrink-0 rounded-full",
    variant === "footer" && "size-auto p-0 hover:bg-transparent",
  );

  const triggerInner = desktopBadge ? (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {avatarNode("sm")}
        <span className="hidden min-w-0 flex-col gap-px text-left sm:flex">
          <span className="truncate text-sm leading-none font-medium">{displayName}</span>
          {topbarSubtitle ? (
            <span className="text-muted-foreground truncate text-xs leading-none">
              {topbarSubtitle}
            </span>
          ) : null}
        </span>
      </span>
      <ChevronDown className="text-muted-foreground size-4 shrink-0 opacity-70" aria-hidden />
    </>
  ) : compact ? (
    avatarNode("sm")
  ) : (
    avatarNode("default")
  );

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setPanel("main");
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={desktopBadge ? "default" : "icon-sm"}
          className={triggerCls}
          aria-label={t("common.accountMenu")}
          data-tour={TOUR_ANCHOR.accountMenu}
        >
          {triggerInner}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("min-w-72 p-2", panel === "help" && "w-80")}>
        {panel === "main" ? (
          <>
            {user?.email ? (
              <>
                <div className="flex items-center gap-3 px-2 py-2.5">
                  {avatarNode("sm")}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{displayName}</div>
                    <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                  </div>
                </div>
                <DropdownMenuSeparator className={accountMenuSeparatorCls} />
              </>
            ) : null}

            {includeChromeShortcuts ? <AccountSettingsMenuItem /> : null}

            {showOrgSwitcher ? (
              <DropdownMenuItem
                className={accountMenuItemCls}
                onSelect={(e) => {
                  e.preventDefault();
                  setSwitchError(null);
                  setPanel("organizations");
                }}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <Building2 className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{t("common.organizations")}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </DropdownMenuItem>
            ) : null}

            {!user?.accessLock ? (
              <DropdownMenuItem className={accountMenuItemCls} onClick={() => navigate("/profile")}>
                <span className="flex min-w-0 items-center gap-1.5">
                  <User className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{t("settings.nav.profile")}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </DropdownMenuItem>
            ) : null}

            <AccountBillingMenuItem />

            <DropdownMenuItem
              className={accountMenuItemCls}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("theme");
              }}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <ThemeMenuIcon mounted={mounted} activeTheme={theme} />
                <span className="truncate">{t("theme.ariaLabel")}</span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </DropdownMenuItem>

            <DropdownMenuItem
              className={accountMenuItemCls}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("language");
              }}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Languages className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{t("common.language")}</span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </DropdownMenuItem>

            {includeChromeShortcuts ? (
              <AccountHelpMenuItem onOpenHelp={() => setPanel("help")} />
            ) : null}

            {productTour && hasNavigableAppAccess(user) ? (
              <DropdownMenuItem
                className={cn(accountMenuItemCls, "justify-start")}
                onSelect={() => productTour.replayAppHomeTour()}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <Compass className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{t("productTour.replay")}</span>
                </span>
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuSeparator className={accountMenuSeparatorCls} />

            <DropdownMenuItem
              className={cn(accountMenuItemCls, "justify-start")}
              variant="destructive"
              onClick={() => void signOutClient().then(() => navigate("/login", { replace: true }))}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <LogOut className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{t("dashboard.logout")}</span>
              </span>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="-mx-2 mt-1 mb-0" />

            <div className="flex h-7 items-center gap-2 px-2 text-muted-foreground">
              <LogoMark className="size-5" />
              <span className="text-xs font-medium">KonstruLog</span>
            </div>
          </>
        ) : null}

        {panel === "organizations" ? (
          <>
            <DropdownMenuItem
              className={cn(accountMenuItemCls, "justify-start")}
              aria-label={t("common.back")}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("main");
              }}
            >
              <ArrowLeft className="size-4 shrink-0" aria-hidden />
              <span className="font-medium">{t("common.organizations")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className={accountMenuSeparatorCls} />
            {organizations.map((org) => {
              const isActive = org.id === user?.organization?.id;
              const invited = org.memberStatus === "invited";
              return (
                <DropdownMenuItem
                  key={org.id}
                  className={accountMenuItemCls}
                  disabled={switching}
                  onSelect={(e) => {
                    e.preventDefault();
                    void onSelectOrganization(org.id);
                  }}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm">{org.name}</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {org.isOwner
                        ? t("common.orgOwner")
                        : org.role === "admin"
                          ? t("common.orgAdmin")
                          : t("common.orgMember")}
                      {invited ? ` · ${t("common.orgInvitePending")}` : null}
                    </span>
                  </span>
                  {isActive ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
                </DropdownMenuItem>
              );
            })}
            {switchError ? (
              <p className="text-destructive px-2 py-1.5 text-xs">{switchError}</p>
            ) : null}
          </>
        ) : null}

        {panel === "theme" ? (
          <>
            <DropdownMenuItem
              className={cn(accountMenuItemCls, "justify-start")}
              aria-label={t("common.back")}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("main");
              }}
            >
              <ArrowLeft className="size-4 shrink-0" aria-hidden />
              <span className="font-medium">{t("theme.ariaLabel")}</span>
            </DropdownMenuItem>
            <DropdownMenuLabel className="text-muted-foreground">
              {t("theme.selectionHint")}
            </DropdownMenuLabel>

            {mounted ? (
              <div className="flex gap-2 px-1.5 pb-1.5">
                {(
                  [
                    { value: "light", icon: Sun, iconClass: "text-amber-500" },
                    { value: "dark", icon: Moon, iconClass: "text-slate-400" },
                    { value: "system", icon: Monitor, iconClass: "text-muted-foreground" },
                  ] as const
                ).map(({ value, icon: Icon, iconClass }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    className={cn(
                      "flex flex-1 cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 p-2 text-center text-xs outline-none transition-colors",
                      themeValue === value
                        ? "border-primary bg-accent/40"
                        : "border-transparent bg-muted/50 hover:bg-muted",
                    )}
                  >
                    <Icon className={cn("size-8", iconClass)} aria-hidden />
                    <span className="font-medium">
                      {value === "light"
                        ? t("theme.light")
                        : value === "dark"
                          ? t("theme.dark")
                          : t("theme.system")}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-1.5 py-2 text-xs text-muted-foreground">{t("common.loading")}</div>
            )}
          </>
        ) : null}

        {panel === "help" ? (
          <>
            <DropdownMenuItem
              className={cn(accountMenuItemCls, "justify-start")}
              aria-label={t("common.back")}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("main");
              }}
            >
              <ArrowLeft className="size-4 shrink-0" aria-hidden />
              <span className="font-medium">{t("help.title")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className={accountMenuSeparatorCls} />
            <HelpMenuItems />
          </>
        ) : null}

        {panel === "language" ? (
          <>
            <DropdownMenuItem
              className={cn(accountMenuItemCls, "justify-start")}
              aria-label={t("common.back")}
              onSelect={(e) => {
                e.preventDefault();
                setPanel("main");
              }}
            >
              <ArrowLeft className="size-4 shrink-0" aria-hidden />
              <span className="font-medium">{t("common.language")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className={accountMenuSeparatorCls} />
            <DropdownMenuRadioGroup
              value={languageValue}
              onValueChange={(lng) => void setUiLanguage(lng)}
            >
              <DropdownMenuRadioItem value="en">English (US)</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="pt-BR">Português (BR)</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
